import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { propertiesAPI, bookingsAPI } from '../services/api';
import { 
  ChevronLeft, 
  Calendar, 
  Users, 
  MapPin, 
  Star, 
  Shield,
  CreditCard,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import { format, differenceInDays, startOfDay, parseISO } from 'date-fns';
import axios from 'axios';

import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  if (imagePath.startsWith('/api/')) return `${BACKEND_URL}${imagePath}`;
  if (imagePath.startsWith('/uploads/')) return `${BACKEND_URL}/api${imagePath}`;
  return imagePath;
};

const BookingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  
  // Get dates from URL params
  const checkIn = searchParams.get('checkIn') || '';
  const checkOut = searchParams.get('checkOut') || '';
  const guests = parseInt(searchParams.get('guests') || '2');
  const roomId = searchParams.get('room_id') || '';
  const cartParam = searchParams.get('cart') || '';

  // Parse cart param: "room_id:qty,room_id:qty" → [{room_id, quantity}]
  const cartSelections = React.useMemo(() => {
    if (!cartParam) return [];
    return cartParam.split(',').map((seg) => {
      const [rid, q] = seg.split(':');
      return { room_id: rid, quantity: parseInt(q || '1', 10) || 1 };
    }).filter((s) => s.room_id);
  }, [cartParam]);

  // Selected rooms detail (multi-room cart OR single room shortcut)
  const cartRoomsDetail = React.useMemo(() => {
    if (!property?.rooms?.length) return [];
    if (cartSelections.length > 0) {
      return cartSelections.map((s) => {
        const room = property.rooms.find((r) => r.room_id === s.room_id);
        return room ? { room, qty: s.quantity } : null;
      }).filter(Boolean);
    }
    if (roomId) {
      const room = property.rooms.find((r) => r.room_id === roomId);
      return room ? [{ room, qty: 1 }] : [];
    }
    return [];
  }, [cartSelections, roomId, property]);

  // Selected room (only for per-room listings)
  const selectedRoom = property?.listing_type === 'rooms' && roomId
    ? (property.rooms || []).find(r => r.room_id === roomId)
    : null;
  
  const [formData, setFormData] = useState({
    checkIn: checkIn,
    checkOut: checkOut,
    guests: guests,
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    specialRequests: ''
  });

  const [priceDetails, setPriceDetails] = useState({
    nights: 0,
    pricePerNight: 0,
    subtotal: 0,
    platformFee: 0,
    gstAmount: 0,
    gstRateLabel: '',
    total: 0
  });
  
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);

  // Prefill guest info from the logged-in user (only fields the user hasn't edited yet)
  useEffect(() => {
    if (!user) return;
    setFormData((prev) => ({
      ...prev,
      guestName: prev.guestName || user.name || '',
      guestEmail: prev.guestEmail || user.email || '',
      guestPhone: prev.guestPhone || (user.phone ? String(user.phone).replace(/\D/g, '').slice(-10) : ''),
    }));
  }, [user]);

  const fetchProperty = useCallback(async () => {
    try {
      setLoading(true);
      const response = await propertiesAPI.getById(id);
      if (response.success) {
        setProperty(response.data);
      }
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Failed to load property');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast.info('Please login or sign up to book a property');
      navigate('/login');
      return;
    }
    if (!authLoading && isAuthenticated) {
      fetchProperty();
    }
  }, [id, isAuthenticated, authLoading, navigate, fetchProperty]);

  // Fetch availability when property is loaded
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const propId = property?.id || property?._id;
        if (!propId) return;
        const res = await axios.get(`${BACKEND_URL}/api/host/availability/${propId}`);
        if (res.data.success && res.data.all_unavailable) {
          setUnavailableDates(res.data.all_unavailable);
        }
      } catch (err) {
        console.error('Failed to fetch availability:', err);
      }
    };
    if (property) fetchAvailability();
  }, [property]);

  useEffect(() => {
    if (property && formData.checkIn && formData.checkOut) {
      calculatePrice();
    }
  }, [property, formData.checkIn, formData.checkOut]);

  const isDateUnavailable = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return unavailableDates.includes(dateStr);
  };

  const isCheckinDisabled = (date) => {
    return date < startOfDay(new Date()) || isDateUnavailable(date);
  };

  const isCheckoutDisabled = (date) => {
    const checkinDate = formData.checkIn ? parseISO(formData.checkIn) : null;
    if (!checkinDate || date <= checkinDate) return true;
    if (date < startOfDay(new Date())) return true;
    if (isDateUnavailable(date)) return true;
    // Block checkout if any date between check-in and checkout is blocked
    let current = new Date(checkinDate);
    current.setDate(current.getDate() + 1);
    while (current < date) {
      if (isDateUnavailable(current)) return true;
      current.setDate(current.getDate() + 1);
    }
    return false;
  };

  const handleCheckinSelect = (date) => {
    if (!date) return;
    const dateStr = format(date, 'yyyy-MM-dd');
    handleInputChange('checkIn', dateStr);
    // Reset checkout if it's now invalid
    if (formData.checkOut) {
      const coDate = parseISO(formData.checkOut);
      if (coDate <= date || isCheckoutDisabled(coDate)) {
        handleInputChange('checkOut', '');
      }
    }
    setCheckInOpen(false);
    setCheckOutOpen(true);
  };

  const handleCheckoutSelect = (date) => {
    if (!date) return;
    handleInputChange('checkOut', format(date, 'yyyy-MM-dd'));
    setCheckOutOpen(false);
  };

  const calculatePrice = () => {
    if (!formData.checkIn || !formData.checkOut || !property) return;
    
    const checkInDate = new Date(formData.checkIn);
    const checkOutDate = new Date(formData.checkOut);
    const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
    
    if (nights <= 0) return;
    
    const pricePerNight = cartRoomsDetail.length > 0
      ? cartRoomsDetail.reduce((sum, x) => sum + x.room.price * x.qty, 0)
      : (selectedRoom ? selectedRoom.price : (property.price || 0));
    const subtotal = pricePerNight * nights;
    const platformFee = Math.round(subtotal * 0.10 * 100) / 100;

    // GST: 5% if room/property per-night ≤ ₹7,500, else 18% — applied per room for multi-room cart
    const GST_LIMIT = 7500;
    let gstAmount = 0;
    const ratesUsed = new Set();
    if (cartRoomsDetail.length > 0) {
      cartRoomsDetail.forEach((x) => {
        const rate = x.room.price <= GST_LIMIT ? 0.05 : 0.18;
        ratesUsed.add(rate);
        gstAmount += x.room.price * x.qty * nights * rate;
      });
    } else if (selectedRoom) {
      const rate = selectedRoom.price <= GST_LIMIT ? 0.05 : 0.18;
      ratesUsed.add(rate);
      gstAmount = selectedRoom.price * nights * rate;
    } else if (property?.price) {
      const rate = property.price <= GST_LIMIT ? 0.05 : 0.18;
      ratesUsed.add(rate);
      gstAmount = subtotal * rate;
    }
    gstAmount = Math.round(gstAmount * 100) / 100;
    const gstRateLabel = ratesUsed.size === 1
      ? `${Math.round([...ratesUsed][0] * 100)}%`
      : 'mixed';

    const total = Math.round((subtotal + platformFee + gstAmount) * 100) / 100;

    setPriceDetails({
      nights,
      pricePerNight,
      subtotal,
      platformFee,
      gstAmount,
      gstRateLabel,
      total
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.checkIn) {
      toast.error('Please select check-in date');
      return false;
    }
    if (!formData.checkOut) {
      toast.error('Please select check-out date');
      return false;
    }
    if (new Date(formData.checkIn) >= new Date(formData.checkOut)) {
      toast.error('Check-out must be after check-in');
      return false;
    }
    if (!formData.guestName.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.guestEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guestEmail)) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (!formData.guestPhone.trim()) {
      toast.error('Please enter your mobile number');
      return false;
    }
    if (!/^\d{10}$/.test(formData.guestPhone.trim())) {
      toast.error('Mobile number must be exactly 10 digits');
      return false;
    }
    return true;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      
      // Create booking
      const bookingResponse = await bookingsAPI.create({
        property_id: id,
        check_in: formData.checkIn,
        check_out: formData.checkOut,
        guests: formData.guests,
        guest_name: formData.guestName,
        guest_email: formData.guestEmail,
        guest_phone: formData.guestPhone,
        special_requests: formData.specialRequests,
        room_id: cartRoomsDetail.length > 1 ? null : (selectedRoom ? selectedRoom.room_id : null),
        room_quantity: cartRoomsDetail.length > 1 ? null : (selectedRoom ? 1 : null),
        room_selections: cartRoomsDetail.length > 1 ? cartRoomsDetail.map((x) => ({ room_id: x.room.room_id, quantity: x.qty })) : null,
      });

      if (!bookingResponse.success) {
        throw new Error('Failed to create booking');
      }

      const { booking_id, razorpay_order_id, razorpay_key_id, amount, mock_mode } = bookingResponse;
      
      // Handle mock mode - simulate successful payment
      if (mock_mode) {
        toast.info('Demo Mode: Simulating payment...');
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Verify mock payment
        const verifyResponse = await bookingsAPI.verifyPayment({
          razorpay_order_id: razorpay_order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: 'mock_signature',
          booking_id: booking_id
        });

        if (verifyResponse.success) {
          setBookingId(booking_id);
          setBookingSuccess(true);
          toast.success('Booking confirmed! (Demo Mode)');
        }
        return;
      }
      
      // Load Razorpay script for real payments
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        return;
      }

      // Open Razorpay checkout
      const options = {
        key: razorpay_key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount,
        currency: 'INR',
        name: 'GeoStays',
        description: `Booking for ${property.title}`,
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            // Verify payment
            const verifyResponse = await bookingsAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: booking_id
            });

            if (verifyResponse.success) {
              setBookingId(booking_id);
              setBookingSuccess(true);
              toast.success('Booking confirmed! Check your email for details.');
            }
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: formData.guestName,
          email: formData.guestEmail,
          contact: formData.guestPhone
        },
        theme: {
          color: '#7CB342'
        },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process booking');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#7CB342]" />
      </div>
    );
  }

  if (bookingSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="shadow-xl">
            <CardContent className="pt-8 text-center">
              <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-green-600 mb-2">Booking Confirmed!</h1>
              <p className="text-gray-600 mb-6">
                Your booking has been confirmed. A confirmation email has been sent to {formData.guestEmail}
              </p>
              
              <div className="bg-gray-50 rounded-lg p-6 text-left mb-6">
                <h3 className="font-semibold mb-4">{property?.title}</h3>
                <div className="space-y-2 text-sm">
                  {selectedRoom && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Room</span>
                      <span className="font-medium">{selectedRoom.category}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Check-in</span>
                    <span className="font-medium">{formData.checkIn}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Check-out</span>
                    <span className="font-medium">{formData.checkOut}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Guests</span>
                    <span className="font-medium">{formData.guests}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="font-medium">₹{priceDetails.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Platform Fee (10%)</span>
                    <span className="font-medium">₹{priceDetails.platformFee.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">GST ({priceDetails.gstRateLabel})</span>
                    <span className="font-medium">₹{priceDetails.gstAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-2">
                    <span className="text-gray-500">Total Paid</span>
                    <span className="font-bold text-lg">₹{priceDetails.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-500 mb-6">
                Booking Reference: <span className="font-mono font-bold">{bookingId}</span>
              </p>

              <div className="flex gap-4">
                <Button 
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="flex-1"
                >
                  Back to Home
                </Button>
                <Button 
                  onClick={() => navigate(`/property/${id}`)}
                  className="flex-1 bg-[#7CB342] hover:bg-[#6da036]"
                >
                  View Property
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Complete your booking</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trip Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-[#7CB342]" />
                  Trip Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Check-in</Label>
                    <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full mt-2 justify-start text-left font-normal"
                          data-testid="booking-checkin-trigger"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {formData.checkIn ? format(parseISO(formData.checkIn), 'MMM dd, yyyy') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={formData.checkIn ? parseISO(formData.checkIn) : undefined}
                          onSelect={handleCheckinSelect}
                          disabled={isCheckinDisabled}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Check-out</Label>
                    <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full mt-2 justify-start text-left font-normal"
                          data-testid="booking-checkout-trigger"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {formData.checkOut ? format(parseISO(formData.checkOut), 'MMM dd, yyyy') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={formData.checkOut ? parseISO(formData.checkOut) : undefined}
                          onSelect={handleCheckoutSelect}
                          disabled={isCheckoutDisabled}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div>
                  <Label htmlFor="guests">Number of Guests</Label>
                  <Input
                    id="guests"
                    type="number"
                    min="1"
                    max={property?.guests || 10}
                    value={formData.guests}
                    onChange={(e) => handleInputChange('guests', parseInt(e.target.value))}
                    className="mt-2 w-32"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Guest Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#7CB342]" />
                  Guest Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="guestName">Full Name *</Label>
                  <Input
                    id="guestName"
                    placeholder="Enter your full name"
                    value={formData.guestName}
                    onChange={(e) => handleInputChange('guestName', e.target.value)}
                    className="mt-2"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="guestEmail">Email *</Label>
                    <Input
                      id="guestEmail"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.guestEmail}
                      onChange={(e) => handleInputChange('guestEmail', e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="guestPhone">Mobile Number *</Label>
                    <Input
                      id="guestPhone"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="10-digit mobile number"
                      value={formData.guestPhone}
                      onChange={(e) => handleInputChange('guestPhone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="mt-2"
                      data-testid="booking-guest-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="specialRequests">Special Requests (Optional)</Label>
                  <Textarea
                    id="specialRequests"
                    placeholder="Any special requests or requirements..."
                    value={formData.specialRequests}
                    onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                    className="mt-2"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Payment Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-[#7CB342]" />
                  Payment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
                  <Shield className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="font-medium">Secure Payment via Razorpay</p>
                    <p className="text-sm text-gray-600">
                      Your payment is protected with industry-standard encryption
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <img src="https://razorpay.com/assets/razorpay-glyph.svg" alt="Razorpay" className="h-8" />
                  <span className="text-xs text-gray-500 self-center">
                    Cards, UPI, Netbanking, Wallets accepted
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Price Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 shadow-lg">
              <CardContent className="pt-6">
                {/* Property Preview */}
                <div className="flex gap-4 pb-4 border-b">
                  <img 
                    src={resolveImageUrl(property?.images?.[0])} 
                    alt={property?.title}
                    className="w-24 h-24 object-cover rounded-lg"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400'; }}
                  />
                  <div className="flex-1">
                    <h3 className="font-medium text-sm line-clamp-2">{property?.title}</h3>
                    {cartRoomsDetail.length > 0 && (
                      <div className="mt-1 space-y-0.5" data-testid="booking-cart-items">
                        {cartRoomsDetail.map((x) => (
                          <Badge key={x.room.room_id} className="bg-[#7CB342]/10 text-[#7CB342] hover:bg-[#7CB342]/10 text-[10px] mr-1 mb-0.5">
                            {x.qty}× {x.room.category}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {cartRoomsDetail.length === 0 && selectedRoom && (
                      <Badge className="mt-1 bg-[#7CB342]/10 text-[#7CB342] hover:bg-[#7CB342]/10 text-[10px]">
                        Room: {selectedRoom.category}
                      </Badge>
                    )}
                    <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">{property?.location}</span>
                    </div>
                    {property?.rating > 0 && (
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm">{property?.rating}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Price Breakdown */}
                <div className="py-4 space-y-3">
                  <h4 className="font-semibold">Price Details</h4>
                  
                  {priceDetails.nights > 0 ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span>₹{priceDetails.pricePerNight.toLocaleString()} × {priceDetails.nights} night{priceDetails.nights > 1 ? 's' : ''}</span>
                        <span>₹{priceDetails.subtotal.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600" data-testid="booking-platform-fee">
                        <span className="inline-flex items-center gap-1">
                          Platform Fee
                          <span className="text-[10px] text-gray-400">(10%)</span>
                        </span>
                        <span>₹{priceDetails.platformFee.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600" data-testid="booking-gst">
                        <span className="inline-flex items-center gap-1">
                          GST
                          <span className="text-[10px] text-gray-400">({priceDetails.gstRateLabel})</span>
                        </span>
                        <span>₹{priceDetails.gstAmount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg pt-3 border-t">
                        <span>Total</span>
                        <span>₹{priceDetails.total.toLocaleString()}</span>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">Select dates to see price</p>
                  )}
                </div>

                {/* Pay Button */}
                <Button
                  onClick={handlePayment}
                  disabled={submitting || priceDetails.nights <= 0}
                  className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white text-lg"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Pay ₹{priceDetails.total.toLocaleString()}
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center mt-3">
                  By clicking Pay, you agree to our Terms and Cancellation Policy
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
